// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

// ==========================================
// INTERVIEW API SERVICE
// Real API Integration
// ==========================================

import {
  SessionValidationResponse,
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  VerifyOTPResponse
} from '../../types/interview';

const API_BASE_URL = '/api/candidate-auth';

export const interviewAPI = {
  /**
   * Validate session token
   * GET /api/candidate-auth/session/:token
   */
  validateSession: async (token: string): Promise<SessionValidationResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/session/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return {
          sessionValid: false,
          status: 'NOT_FOUND'
        };
      }

      const data: SessionValidationResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[Interview API] Session validation error:', error);
      return {
        sessionValid: false,
        status: 'INVALID'
      };
    }
  },

  /**
   * Send OTP
   * POST /api/candidate-auth/otp/send
   */
  sendOTP: async (request: SendOTPRequest): Promise<SendOTPResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: request.token,
          workEmail: request.workEmail
        })
      });

      const data: SendOTPResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[Interview API] Send OTP error:', error);
      return {
        success: false,
        message: 'Failed to send verification code. Please try again.'
      };
    }
  },

  /**
   * Verify OTP
   * POST /api/candidate-auth/otp/verify
   */
  verifyOTP: async (request: VerifyOTPRequest): Promise<VerifyOTPResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: request.token,
          otp: request.otp
        })
      });

      const data: VerifyOTPResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[Interview API] Verify OTP error:', error);
      return {
        verified: false,
        message: 'Failed to verify code. Please try again.',
        sessionId: request.token
      };
    }
  },

  resendOTP: async (request: SendOTPRequest): Promise<SendOTPResponse> => {
    return interviewAPI.sendOTP(request);
  },

  recordConsent: async (token: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          recordingConsent: true
        })
      });
      return await response.json();
    } catch (error) {
      console.error('[Interview API] Consent recording error:', error);
      return {
        success: false,
        message: 'Failed to record consent. Please try again.'
      };
    }
  },

  /**
   * Notify the backend that the candidate's session has ended, triggering
   * completion/finalization (transcript persistence, assessment, recommendation).
   * POST /api/candidate-auth/session/complete
   */
  completeSession: async (
    token: string
  ): Promise<{ success: boolean; status?: string; sessionId?: string; message?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/session/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        keepalive: true,
        body: JSON.stringify({ token })
      });
      return await response.json();
    } catch (error) {
      console.error('[Interview API] Complete session error:', error);
      return {
        success: false,
        message: 'Failed to complete session.'
      };
    }
  }
};
